import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Edit2, Trash2, Building2, Wallet, 
  TrendingUp, TrendingDown, History, RefreshCw, ArrowUpDown, Zap, Save, X
} from 'lucide-react';
import { api } from '../App';
import { formatINR, stripCommas } from '../lib/numberFormat';

const Banks = ({ user, setUser }) => {
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [activeBankFilter, setActiveBankFilter] = useState('all');
  const [editMode, setEditMode] = useState(false);
  const [quickEditMode, setQuickEditMode] = useState(false);
  const [drafts, setDrafts] = useState({}); // {accountId: {available_balance?, used_amount?, od_limit?}}
  const [savingBulk, setSavingBulk] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'bank_name', direction: 'asc' });

  const [formData, setFormData] = useState({
    member_id: '',
    bank_name: 'HDFC',
    account_type: 'savings',
    account_number: '',
    account_holder_name: '',
    ifsc_code: '',
    available_balance: 0,
    used_amount: 0,
    od_limit: 0,
    notes: ''
  });

  useEffect(() => {
    fetchAccounts();
    fetchMembers();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/bank-accounts');
      setAccounts(res.data);
    } catch (err) {
      toast.error('Failed to fetch bank accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await api.get('/family-members');
      setMembers(res.data);
    } catch (err) {
      console.error('Failed to fetch members');
    }
  };

  const fetchHistory = async (accountId) => {
    try {
      const res = await api.get(`/bank-accounts/${accountId}/history`);
      setSelectedHistory(res.data);
      setHistoryDialogOpen(true);
    } catch (err) {
      toast.error('Failed to fetch balance history');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await api.put(`/bank-accounts/${editingAccount.id}`, formData);
        toast.success('Bank account updated');
      } else {
        await api.post('/bank-accounts', formData);
        toast.success('Bank account created');
      }
      setDialogOpen(false);
      resetForm();
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;
    try {
      await api.delete(`/bank-accounts/${id}`);
      toast.success('Account deleted');
      fetchAccounts();
    } catch (err) {
      toast.error('Failed to delete account');
    }
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      member_id: account.member_id || '',
      bank_name: account.bank_name,
      account_type: account.account_type,
      account_number: account.account_number,
      account_holder_name: account.account_holder_name,
      ifsc_code: account.ifsc_code || '',
      available_balance: account.available_balance,
      used_amount: account.used_amount,
      od_limit: account.od_limit,
      notes: account.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      member_id: '',
      bank_name: 'HDFC',
      account_type: 'savings',
      account_number: '',
      account_holder_name: '',
      ifsc_code: '',
      available_balance: 0,
      used_amount: 0,
      od_limit: 0,
      notes: ''
    });
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member?.name || 'Unlinked';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Quick Update helpers
  const getDraftValue = (account, field) => {
    const d = drafts[account.id];
    if (d && field in d) return d[field];
    return account[field] ?? 0;
  };

  const setDraftValue = (accountId, field, rawValue) => {
    setDrafts(prev => {
      const next = { ...prev };
      const row = { ...(next[accountId] || {}) };
      if (rawValue === '' || rawValue === null) {
        delete row[field];
      } else {
        row[field] = rawValue;
      }
      if (Object.keys(row).length === 0) {
        delete next[accountId];
      } else {
        next[accountId] = row;
      }
      return next;
    });
  };

  const isRowDirty = (account) => {
    const d = drafts[account.id];
    if (!d) return false;
    for (const f of ['available_balance', 'used_amount', 'od_limit']) {
      if (f in d && parseFloat(d[f] || 0) !== parseFloat(account[f] || 0)) return true;
    }
    return false;
  };

  const dirtyCount = Object.keys(drafts).filter(id => {
    const acc = accounts.find(a => a.id === id);
    return acc && isRowDirty(acc);
  }).length;

  const toggleQuickEdit = () => {
    if (quickEditMode && dirtyCount > 0) {
      if (!window.confirm(`Discard ${dirtyCount} unsaved changes?`)) return;
    }
    setDrafts({});
    setQuickEditMode(!quickEditMode);
  };

  const saveBulkBalances = async () => {
    const updates = [];
    for (const acc of accounts) {
      if (!isRowDirty(acc)) continue;
      const d = drafts[acc.id] || {};
      const row = { id: acc.id };
      for (const f of ['available_balance', 'used_amount', 'od_limit']) {
        if (f in d) row[f] = parseFloat(d[f]) || 0;
      }
      updates.push(row);
    }
    if (updates.length === 0) {
      toast.info('No changes to save');
      return;
    }
    try {
      setSavingBulk(true);
      const res = await api.put('/bank-accounts/bulk-balance', { updates });
      const { updated, history_recorded, errors } = res.data;
      if (errors && errors.length > 0) {
        toast.warning(`Updated ${updated}, ${errors.length} errors`);
      } else {
        toast.success(`Updated ${updated} accounts · ${history_recorded} history entries`);
      }
      setDrafts({});
      await fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingBulk(false);
    }
  };

  // Filter accounts
  let filteredAccounts = accounts.filter(acc => {
    if (activeTab !== 'all' && acc.account_type !== activeTab) return false;
    if (activeBankFilter !== 'all' && acc.bank_name !== activeBankFilter) return false;
    return true;
  });

  // Sort accounts
  filteredAccounts = [...filteredAccounts].sort((a, b) => {
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // Handle member_id - sort by member name
    if (sortConfig.key === 'member_id') {
      aVal = getMemberName(a.member_id).toLowerCase();
      bVal = getMemberName(b.member_id).toLowerCase();
    }
    
    // Handle string comparison
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = (bVal || '').toLowerCase();
    }
    
    // Handle null/undefined
    if (aVal === null || aVal === undefined) aVal = 0;
    if (bVal === null || bVal === undefined) bVal = 0;
    
    if (sortConfig.direction === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  // Calculate totals
  const totals = {
    savings: accounts.filter(a => a.account_type === 'savings').reduce((sum, a) => sum + a.available_balance, 0),
    loan_available: accounts.filter(a => a.account_type === 'loan').reduce((sum, a) => sum + a.available_balance, 0),
    loan_used: accounts.filter(a => a.account_type === 'loan').reduce((sum, a) => sum + a.used_amount, 0),
    loan_limit: accounts.filter(a => a.account_type === 'loan').reduce((sum, a) => sum + a.od_limit, 0),
  };

  const uniqueBanks = [...new Set(accounts.map(a => a.bank_name))];

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-primary" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Bank Accounts</h1>
            <p className="text-gray-600">Manage loan and savings accounts</p>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="banks-toolbar">
            <Button 
              onClick={toggleQuickEdit} 
              variant={quickEditMode ? 'default' : 'outline'}
              size="sm"
              className={quickEditMode ? 'bg-amber-500 hover:bg-amber-600' : ''}
              data-testid="quick-update-toggle"
            >
              <Zap size={16} className="mr-2" />
              {quickEditMode ? 'Cancel Quick Update' : 'Quick Balance Update'}
            </Button>
            {quickEditMode && (
              <Button
                onClick={saveBulkBalances}
                disabled={savingBulk || dirtyCount === 0}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-bulk-btn"
              >
                <Save size={16} className="mr-2" />
                {savingBulk ? 'Saving...' : `Save All${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}
              </Button>
            )}
            <Button 
              onClick={() => setEditMode(!editMode)} 
              variant={editMode ? 'default' : 'outline'}
              size="sm"
            >
              <Edit2 size={16} className="mr-2" />
              {editMode ? 'Done Editing' : 'Edit Mode'}
            </Button>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary">
              <Plus size={18} className="mr-2" />
              Add Account
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card bg-green-50 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <Wallet className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-green-700">Savings Balance</p>
                <p className="text-xl font-bold text-green-800">{formatCurrency(totals.savings)}</p>
              </div>
            </div>
          </div>
          
          <div className="card bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <TrendingUp className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-blue-700">Loan Available</p>
                <p className="text-xl font-bold text-blue-800">{formatCurrency(totals.loan_available)}</p>
              </div>
            </div>
          </div>

          <div className="card bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <TrendingDown className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-amber-700">Loan Used</p>
                <p className="text-xl font-bold text-amber-800">{formatCurrency(totals.loan_used)}</p>
              </div>
            </div>
          </div>

          <div className="card bg-purple-50 border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Building2 className="text-white" size={20} />
              </div>
              <div>
                <p className="text-sm text-purple-700">Total OD Limit</p>
                <p className="text-xl font-bold text-purple-800">{formatCurrency(totals.loan_limit)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex flex-wrap gap-4">
            <div className="flex gap-2">
              <Button
                variant={activeTab === 'all' ? 'default' : 'outline'}
                onClick={() => setActiveTab('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={activeTab === 'savings' ? 'default' : 'outline'}
                onClick={() => setActiveTab('savings')}
                size="sm"
              >
                Savings
              </Button>
              <Button
                variant={activeTab === 'loan' ? 'default' : 'outline'}
                onClick={() => setActiveTab('loan')}
                size="sm"
              >
                Loan/OD
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={activeBankFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setActiveBankFilter('all')}
                size="sm"
              >
                All Banks
              </Button>
              {uniqueBanks.map(bank => (
                <Button
                  key={bank}
                  variant={activeBankFilter === bank ? 'default' : 'outline'}
                  onClick={() => setActiveBankFilter(bank)}
                  size="sm"
                >
                  {bank}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {quickEditMode && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="quick-update-hint">
            <div className="flex items-center gap-2">
              <Zap size={18} className="text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Quick Balance Update mode is ON</p>
                <p className="text-xs text-amber-700">Edit balances inline. Modified rows are highlighted. Only changed rows are saved. History is recorded automatically.</p>
              </div>
            </div>
            <div className="flex gap-2">
              {dirtyCount > 0 && (
                <Button
                  onClick={() => { if (window.confirm('Discard all changes?')) setDrafts({}); }}
                  variant="outline" size="sm"
                >
                  <X size={14} className="mr-1" /> Reset ({dirtyCount})
                </Button>
              )}
              <Button
                onClick={saveBulkBalances}
                disabled={savingBulk || dirtyCount === 0}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Save size={14} className="mr-1" />
                {savingBulk ? 'Saving...' : `Save ${dirtyCount} ${dirtyCount === 1 ? 'change' : 'changes'}`}
              </Button>
            </div>
          </div>
        )}

        {/* Accounts Table */}
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Bank</th>
                <th className="text-left py-3 px-4">Account Holder</th>
                <th className="text-left py-3 px-4">Account #</th>
                <th className="text-left py-3 px-4">Type</th>
                <th 
                  className="text-left py-3 px-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('member_id')}
                >
                  <div className="flex items-center gap-1">
                    Member <ArrowUpDown size={14} className="text-gray-400" />
                  </div>
                </th>
                <th 
                  className="text-right py-3 px-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('available_balance')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Balance/Available <ArrowUpDown size={14} className="text-gray-400" />
                  </div>
                </th>
                <th 
                  className="text-right py-3 px-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('used_amount')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Used <ArrowUpDown size={14} className="text-gray-400" />
                  </div>
                </th>
                <th 
                  className="text-right py-3 px-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('od_limit')}
                >
                  <div className="flex items-center justify-end gap-1">
                    OD Limit <ArrowUpDown size={14} className="text-gray-400" />
                  </div>
                </th>
                <th className="text-center py-3 px-4 sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] z-10 w-[110px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.map((account) => (
                <tr key={account.id} className="border-b hover:bg-gray-50 group">
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      account.bank_name === 'HDFC' ? 'bg-blue-100 text-blue-800' : 
                      account.bank_name === 'IDBI' ? 'bg-green-100 text-green-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {account.bank_name}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium">{account.account_holder_name}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{account.account_number}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      account.account_type === 'savings' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {account.account_type === 'savings' ? 'Savings' : 'Loan/OD'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm">{getMemberName(account.member_id)}</td>
                  <td className="py-3 px-4 text-right font-medium text-green-600">
                    {quickEditMode ? (
                      <input
                        type="text" inputMode="decimal"
                        value={formatINR(getDraftValue(account, 'available_balance'))}
                        onChange={(e) => setDraftValue(account.id, 'available_balance', stripCommas(e.target.value))}
                        className={`w-32 px-2 py-1 text-right border rounded text-sm ${isRowDirty(account) ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                        data-testid={`qb-avail-${account.id}`}
                      />
                    ) : formatCurrency(account.available_balance)}
                  </td>
                  <td className="py-3 px-4 text-right text-amber-600">
                    {quickEditMode && account.account_type === 'loan' ? (
                      <input
                        type="text" inputMode="decimal"
                        value={formatINR(getDraftValue(account, 'used_amount'))}
                        onChange={(e) => setDraftValue(account.id, 'used_amount', stripCommas(e.target.value))}
                        className={`w-32 px-2 py-1 text-right border rounded text-sm ${isRowDirty(account) ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                        data-testid={`qb-used-${account.id}`}
                      />
                    ) : (account.account_type === 'loan' ? formatCurrency(account.used_amount) : '-')}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-600">
                    {quickEditMode && account.account_type === 'loan' ? (
                      <input
                        type="text" inputMode="decimal"
                        value={formatINR(getDraftValue(account, 'od_limit'))}
                        onChange={(e) => setDraftValue(account.id, 'od_limit', stripCommas(e.target.value))}
                        className={`w-32 px-2 py-1 text-right border rounded text-sm ${isRowDirty(account) ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}`}
                        data-testid={`qb-od-${account.id}`}
                      />
                    ) : (account.account_type === 'loan' ? formatCurrency(account.od_limit) : '-')}
                  </td>
                  <td className="py-3 px-4 sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] z-[5] w-[110px]">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => fetchHistory(account.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="View History"
                      >
                        <History size={16} className="text-gray-500" />
                      </button>
                      {editMode && (
                        <>
                          <button
                            onClick={() => openEditDialog(account)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Edit2 size={16} className="text-blue-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(account.id)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Delete"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredAccounts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No bank accounts found. Add your first account to get started.
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Account Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bank Name</Label>
                <Select value={formData.bank_name} onValueChange={(v) => setFormData({...formData, bank_name: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HDFC">HDFC</SelectItem>
                    <SelectItem value="IDBI">IDBI</SelectItem>
                    <SelectItem value="ICICI">ICICI</SelectItem>
                    <SelectItem value="SBI">SBI</SelectItem>
                    <SelectItem value="Axis">Axis</SelectItem>
                    <SelectItem value="Kotak">Kotak</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Type</Label>
                <Select value={formData.account_type} onValueChange={(v) => setFormData({...formData, account_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="loan">Loan/OD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Account Holder Name</Label>
              <Input
                value={formData.account_holder_name}
                onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account Number</Label>
                <Input
                  value={formData.account_number}
                  onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input
                  value={formData.ifsc_code}
                  onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Link to Family Member</Label>
              <Select value={formData.member_id || 'none'} onValueChange={(v) => setFormData({...formData, member_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select member (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Available Balance (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.available_balance}
                  onChange={(e) => setFormData({...formData, available_balance: parseFloat(e.target.value) || 0})}
                />
              </div>
              {formData.account_type === 'loan' && (
                <>
                  <div>
                    <Label>Used Amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.used_amount}
                      onChange={(e) => setFormData({...formData, used_amount: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>OD Limit (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.od_limit}
                      onChange={(e) => setFormData({...formData, od_limit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Optional notes..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary flex-1">
                {editingAccount ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Balance History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Balance History</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {selectedHistory.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No balance changes recorded yet.</p>
            ) : (
              selectedHistory.map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`font-medium ${item.change_amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.change_amount >= 0 ? '+' : ''}{formatCurrency(item.change_amount)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(item.created_at).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {formatCurrency(item.previous_balance)} → {formatCurrency(item.new_balance)}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Banks;
